import { useState, useCallback } from "react";
import {
  ApiDataResponse,
  ApiResponse,
  Level,
  PromotionRule,
} from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import useAxios from "../useAxios";
import { useSchoolsContext } from "../../Contexts/schoolContext";
import { useApiResponseHandler } from "../useApiResponseHandler";

export const useLevelService = () => {
  const { setLevels, setPromotionRules } = useSchoolsContext();
  const axiosClient = useAxios();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });

  const createLevelWithPromotionRule = useCallback(
    async (
      data: Level,
      onSuccess?: (level: Level) => void
    ): Promise<{ result: ApiDataResponse<Level> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = (await new ApiService<Level>(
          "/levels/with-promotion-rule",
          axiosClient
        ).create(data)) as ApiDataResponse<Level>;
        if (result.succeeded && result.data) {
          const createdLevel = result.data;

          // If we have a promotion rule, add it to both the level and the promotionRules state
          if (data.promotionRule) {
            const promotionRule = {
              ...data.promotionRule,
              levelId: createdLevel.id,
            } as PromotionRule;
            createdLevel.promotionRule = promotionRule;
            setPromotionRules((prev) => [...prev, promotionRule]);
          }

          // Add the level with its associated promotion rule
          setLevels((prev: Level[]) =>
            [...prev, createdLevel].sort((a, b) => a.order - b.order)
          );

          handleApiSuccess("Level created successfully.");
          onSuccess?.(createdLevel);
          return { result };
        } else {
          handleApiError(result, "Failed to create level");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to create level");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to create level",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as unknown as Level,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess]
  );

  const updateLevelWithPromotionRule = useCallback(
    async (
      id: string,
      data: Level,
      onSuccess?: (level: Level) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Level>(
          `/levels/${id}/with-promotion-rule`,
          axiosClient
        ).update("", data);
        if (result.succeeded) {
          // For update, we use the input data since result doesn't contain the updated level
          const updatedLevel = { ...data, id } as Level;

          // Handle promotion rules
          if (data.promotionRule) {
            const promotionRule = {
              ...data.promotionRule,
              levelId: id,
            } as PromotionRule;
            updatedLevel.promotionRule = promotionRule;

            // Update or add promotion rule
            setPromotionRules((prev) => {
              const existingRuleIndex = prev.findIndex(
                (rule) => rule.levelId === id
              );
              if (existingRuleIndex >= 0) {
                // Update existing rule
                return prev.map((rule) =>
                  rule.levelId === id ? promotionRule : rule
                );
              } else {
                // Add new rule
                return [...prev, promotionRule];
              }
            });
          } else {
            // Remove promotion rule if it was disabled
            updatedLevel.promotionRule = undefined;
            setPromotionRules((prev) =>
              prev.filter((rule) => rule.levelId !== id)
            );
          }

          // Update the level with its promotion rule status
          setLevels((prev) =>
            prev
              .map((l) => (l.id === id ? updatedLevel : l))
              .sort((a, b) => a.order - b.order)
          );

          handleApiSuccess("Level updated successfully");
          onSuccess?.(updatedLevel);
          return { result };
        } else {
          handleApiError(result, "Failed to update level");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update level");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update level",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess]
  );

  const deleteLevel = useCallback(
    async (
      id: string,
      onSuccess?: (id: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Level>(
          "/levels",
          axiosClient
        ).delete(id);
        if (result.succeeded) {
          setLevels((prev: Level[]) =>
            prev.filter((l) => l.id !== id).sort((a, b) => a.order - b.order)
          );
          setPromotionRules((prev) =>
            prev.filter((rule) => rule.levelId !== id)
          );
          handleApiSuccess("Level deleted successfully");
          onSuccess?.(id);
          return { result };
        } else {
          handleApiError(result, "Failed to delete level");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to delete level");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to delete level",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
        setOpenDeleteDialog(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess]
  );

  return {
    createLevelWithPromotionRule,
    updateLevelWithPromotionRule,
    deleteLevel,
    isLoading,
    errors,
    setErrors,
    clearErrors,
    openDeleteDialog,
    setOpenDeleteDialog,
  };
};
